import { useMemo } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { stepLabels } from "src/utils/quote/quoteConstants";
import ProgressArc from "./ProgressArc";
import StepDotList from "./StepDotList";
import { useT } from "src/i18n";

export default function QuoteStepper({ activeStep, maxReachedStep, onJumpTo }) {
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down(820));
    const t = useT();
    const STEP_LABELS = useMemo(() => stepLabels(t), [t]);
    const label = (STEP_LABELS[activeStep] ?? "").toUpperCase();
    const total = STEP_LABELS.length;

    if (isCompact) {
        // Mobile: a single slim row — the tappable horizontal dot stepper plus a
        // compact "N / total" readout. The big ProgressArc is intentionally dropped
        // here: it stacked a second ~84px progress indicator above the dots and pushed
        // the form down a third of the viewport. The step heading already names the
        // current step, so the circle is redundant on mobile. The arc stays on the
        // desktop sidebar (below) where there's room. Step-jumping is preserved.
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <StepDotList
                        labels={STEP_LABELS}
                        currentStep={activeStep}
                        maxReachedStep={maxReachedStep}
                        onJumpTo={onJumpTo}
                        orientation="horizontal"
                    />
                </Box>
                <Box
                    aria-label={t("wizard.quoteStepper.stepAriaLabel", { current: activeStep + 1, total })}
                    sx={{
                        flexShrink: 0,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 11,
                        letterSpacing: "0.10em",
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                    }}
                >
                    {activeStep + 1} / {total}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 3.5,
            pt: 1,
        }}>
            <Box sx={{ alignSelf: "center" }}>
                <ProgressArc current={activeStep + 1} total={total} label={label} size={88} />
            </Box>
            <StepDotList
                labels={STEP_LABELS}
                currentStep={activeStep}
                maxReachedStep={maxReachedStep}
                onJumpTo={onJumpTo}
            />
        </Box>
    );
}
